<template>
  <div>
    <page-header
      :actions="pageActionConfig"
      left-side-class="col-xl-5"
      :page="pageConfig"
      right-side-class="col-xl-7 align-items-end d-flex pb-3 justify-content-end"
    />
    <InnerPageMessage
      v-for="(sampleLog, index) in sampleLogs"
      :key="`job-sample-${index}`"
      class="mb-4"
      :custom-background-color="getSampleLogStyles(sampleLog).backgroundColor"
      :custom-border-color="getSampleLogStyles(sampleLog).borderColor"
      :custom-color="getSampleLogStyles(sampleLog).color"
      :custom-icon="getSampleLogStyles(sampleLog).icon"
      :custom-icon-color="getSampleLogStyles(sampleLog).iconColor"
      :hyperlink-config="getHyperlinkConfig(sampleLog)"
      :message="jobSampleMessage(sampleLog)"
      :visible="true"
    />
    <create-sample-card
      v-if="isCreateSampleMode"
      :academic-year-options="filterOptions.academicYears"
      :area-options="availableAreaForCreateSample"
      :semester-type-options="filterOptions.semesters"
      @on-close="setCreateSampleMode"
      @on-continue="onCreateSample"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @academicYear="onChangeAcademicYear"
      @center="onChangeCenter"
      @locationType="onChangeLocationType"
      @municipality="onChangeMunicipality"
      @provider="onChangeProvider"
      @search="onSearchHandler"
    />
    <questionnaire-status-bar
      v-if="showQuestionnaireIndicator"
      :catalog="questionnaireStatusCatalog"
    />
    <custom-separator class="my-4" text="Resultados" />
    <action-button
      v-if="isUniqueAreaTypeSelected"
      class="ml-2 px-4"
      i="fas fa-file-exclamation"
      type="button"
      variant="outline-success close-border mr-n3"
      @click="
        goToSampleDetailsHandler({
          areaId: appliedFilters.areaId,
          academicYearId: appliedFilters.academicYearId,
          periodNumber: appliedFilters.semesterId,
        })
      "
    >
      {{ hyperlinkConfig.text }}
    </action-button>
    <custom-result-not-found
      v-if="resultIsEmpty"
      text="No hay información disponible."
    />
    <div v-for="row of results" :key="row.id" class="py-2">
      <result-row :data="row" @on-cancel="onCancelHandler" />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
    <div v-else class="mb-5" />
  </div>
</template>

<script>
//libs
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import QuestionnaireStatusBar from './components/QuestionnaireStatusBar.vue';
import CreateSampleCard from './components/CreateSampleCard.vue';
import alertMixin from 'utils/mixins/alert.mixin';
import storeMixin from '@/shared/mixins/storeGenerator';
import { questionnaireArea } from '@/utils/constants/questionnaireArea.js';

//services
import {
  getInitFilters as _getInitFilters,
  getDisciplineList as _getDisciplineList,
  getServicesByAreaId as _getServicesByAreaId,
  getPorters as _getPorters,
  getschool as _getschool,
  getProviderMunicipalities as _getProviderMunicipalities,
  getServiceLocations as _getServiceLocations,
  getMunicipalities as _getMunicipalities,
  search as _search,
  searchTecnicalAssistanceQuestionnaires as _searchTecnicalAssistanceQuestionnaires,
  getQuestionnaireStatusBarInfo as _getQuestionnaireStatusBarInfo,
  createQuestionnaireSample as _createQuestionnaireSample,
  validateSampleSelection as _validateSampleSelection,
  getGroupedSampleExecutionLogs as _getGroupedSampleExecutionLogs,
  getSampleLog as _getSampleLog,
} from '@/modules/serviceSatisfactionQuestionnaire/api.js';

import {
  providersList as _getProviders,
  getProviderCenters as _getProviderCenters,
  getProviderSpecialists as _getProviderSpecialists,
} from '@/modules/shared/provider.api.js';

import { getServiceLocationTypes as _getServiceLocationTypes } from '@/modules/provider/api.js';

//components
import ResultRow from './components/ResultRow.vue';
import InnerPageMessage from '@/components/common/page/InnerPageMessage.vue';

//constants
const DEFAULT_PAGE_SIZE = 20;
const Maestro = 2;
const Transportacion = 2;
const FirstSemester = 1;

export default {
  name: 'Main',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    Pagination,
    QuestionnaireStatusBar,
    CreateSampleCard,
    InnerPageMessage,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    filterModel: {
      academicYear: null,
      semester: null,
      questionnaireType: null,
      status: null,
      provider: null,
      locationType: null,
      center: null,
      municipality: null,
      locationService: null,
      specialist: null,
      serviceType: null,
      teacher: null,
      studentSieId: null,
      emailSent: null,
      ticketNumber: null,
    },
    filterOptions: {
      academicYears: [],
      semesters: [],
      areas: [],
      questionnaireTypes: [],
      statuses: [],
      providers: [],
      locationTypes: [],
      centers: [],
      centersOriginal: [],
      municipalities: [],
      locationServices: [],
      specialists: [],
      serviceTypes: [],
      teachers: [],
      emailSentList: [],
    },
    results: [],
    sampleLogs: [],
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    questionnaireStatusCatalog: {
      pendings: 0,
      completed: 0,
      totalCompleted: 0,
    },
    appliedFilters: {},
    showQuestionnaireIndicator: false,
    isCreateSampleMode: false,
    selectTrasportation: false,
  }),
  computed: {
    pageConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Cuestionario de Satisfacción de Servicios',
        routes: ['Proveedor', 'Cuestionario de Satisfacción de Servicios'],
      };
    },
    pageActionConfig() {
      return [
        {
          text: 'Actualizar Indicadores',
          icon: 'fas fa-search-plus',
          variant: 'outline-success',
          visible: false,
          click: () => this.refreshStatusBar(),
        },
        {
          text: 'Informes',
          icon: 'fas fa-folders',
          variant: 'outline-success',
          visible: this.userHasPermissions(
            'provider.satisfactionquestionnaire.view.reports'
          ),
          click: () => {},
        },
        {
          text: 'Crear Muestra',
          icon: 'fas fa-plus-circle',
          variant: 'success',
          visible:
            !this.isCreateSampleMode &&
            this.userHasPermissions(
              'provider.satisfactionquestionnaire.createsample'
            ),
          click: () => this.setCreateSampleMode(true),
        },
      ];
    },
    resultIsEmpty() {
      return this.results.length === 0;
    },
    currentAcademicYearId() {
      return (this.currentAcademicYear() || {}).id;
    },
    disableCompanyFilter() {
      return this.filterOptions.providers?.length === 1;
    },
    availableAreaForCreateSample() {
      const areaAvailables = [
        questionnaireArea.EvaluationTherapy,
        questionnaireArea.Transportation,
      ];

      return this.filterOptions.areas.filter((x) =>
        areaAvailables.includes(x.id)
      );
    },
    companyInitialValue() {
      return this.filterOptions.providers?.length === 1
        ? this.filterOptions.providers[0]
        : null;
    },
    disableCenterFilter() {
      return this.filterOptions.centers?.length === 1;
    },
    centerInitialValue() {
      return this.filterOptions.centers?.length === 1
        ? this.filterOptions.centers[0]
        : null;
    },
    filtersConfig() {
      let filters = [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          initialValue: this.currentAcademicYear(),
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: false,
        },
        {
          name: 'area',
          component: 'CustomDropDown',
          options: this.filterOptions.areas,
          placeholder: 'Área',
          onChanged: this.filterServices,
          fieldName: 'name',
          fieldKey: 'id',
          validations: 'required',
          error: 'Favor seleccionar un área.',
          class: 'col-12 col-md-3',
          clearable: false,
        },
        {
          name: 'semester',
          component: 'CustomDropDown',
          options: this.filterOptions.semesters,
          placeholder: 'Semestre',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: false,
          validations: 'required',
          error: 'Favor de seleccionar un semestre.',
        },
        {
          name: 'questionnaireType',
          component: 'CustomDropDown',
          options: this.filterOptions.questionnaireTypes,
          placeholder: 'Tipo de Cuestionario',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },

        //ADVANCED FILTERS
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          label: 'Tipo de servicio:',
          options: this.filterOptions.serviceTypes,
          placeholder: 'Tipo de Servicio',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'porter',
          component: 'CustomDropDown',
          label: 'Porteador:',
          visible: this.selectTrasportation,
          options: this.filterOptions.porters,
          placeholder: 'Porteador',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          initialValue: this.companyInitialValue,
          label: 'Proveedor:',
          visible: !this.selectTrasportation,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          disabled: this.disableCompanyFilter,
        },
        {
          name: 'locationType',
          component: 'CustomDropDown',
          options: this.filterOptions.locationTypes,
          label: 'Tipo de lugar:',
          visible: !this.selectTrasportation,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.filterOptions.centers,
          initialValue: this.centerInitialValue,
          label: !this.selectTrasportation ? 'Centro:' : 'CSEE:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          disabled: this.disableCenterFilter,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filterOptions.municipalities,
          label: 'Municipio:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          label: 'Escuela:',
          visible: this.selectTrasportation,
          options: this.filterOptions.schoolList,
          placeholder: 'Escuela',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'locationService',
          component: 'CustomDropDown',
          options: this.filterOptions.locationServices,
          label: 'Lugar de Servicio:',
          visible: !this.selectTrasportation,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterOptions.specialists,
          label: 'Nombre del Especialista:',
          visible: !this.selectTrasportation,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'teacher',
          component: 'CustomDropDown',
          options: this.filterOptions.teachers,
          label: 'Nombre del Maestro:',
          visible: !this.selectTrasportation,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'studentSieId',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          label: 'Número de SIE:',
          placeholder: 'Escriba Número de SIE',
          class: 'col-12 col-md-3',
          maskType: 'SIE',
          isAdvanced: true,
        },
        {
          name: 'disciplineList',
          component: 'CustomDropDown',
          label: 'Disciplina:',
          visible: this.selectTrasportation,
          options: this.filterOptions.disciplineList,
          placeholder: 'Disciplina',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          label: 'Estatus:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'emailSent',
          component: 'CustomDropDown',
          options: this.filterOptions.emailSentList,
          label: 'Correos Enviados:',
          visible: !this.selectTrasportation,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
      ];

      if (this.filterModel.area?.id == questionnaireArea.TecnicalAssistance) {
        filters = [
          {
            name: 'academicYear',
            component: 'CustomDropDown',
            options: this.filterOptions.academicYears,
            initialValue: this.currentAcademicYear(),
            placeholder: 'Año Académico',
            fieldName: 'name',
            fieldKey: 'id',
            class: 'col-12 col-md-3',
            clearable: false,
          },
          {
            name: 'area',
            component: 'CustomDropDown',
            options: this.filterOptions.areas,
            placeholder: 'Área',
            onChanged: this.filterServices,
            fieldName: 'name',
            fieldKey: 'id',
            validations: 'required',
            error: 'Favor seleccionar un área.',
            class: 'col-12 col-md-3',
            clearable: false,
          },
          {
            name: 'semester',
            component: 'CustomDropDown',
            options: this.filterOptions.semesters,
            placeholder: 'Semestre',
            fieldName: 'name',
            fieldKey: 'id',
            class: 'col-12 col-md-3',
            clearable: false,
          },
          {
            name: 'questionnaireType',
            component: 'CustomDropDown',
            options: this.filterOptions.questionnaireTypes,
            placeholder: 'Tipo de Cuestionario',
            fieldName: 'name',
            fieldKey: 'id',
            class: 'col-12 col-md-3',
            clearable: true,
          },

          //ADVANCED FILTERS
          {
            name: 'status',
            component: 'CustomDropDown',
            options: this.filterOptions.statuses,
            label: 'Estatus:',
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            class: 'col-12 col-md-3',
            clearable: true,
            isAdvanced: true,
          },
          {
            name: 'ticketNumber',
            component: 'CustomInput',
            icon: 'fas fa-search color-primary-2',
            leftIcon: true,
            label: 'Número de Ticket:',
            placeholder: 'Escriba Número de Ticket',
            class: 'col-12 col-md-3',
            isAdvanced: true,
          },
        ];
      }

      return filters;
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    jobSampleMessage() {
      return (log) =>
        log?.isRunning
          ? `Se envió la creación de la muestra [${log.academicYear}-${log.areaName}-${log.period}] exitosamente, esto puede tardar varios minutos, puede regresar más tarde para la confirmación de la muestra.`
          : log?.isConfirming
          ? `Se comenzó la creación de los cuestionarios [${log.academicYear}-${log.areaName}-${log.period}], esto puede tardar varios minutos, puede regresar más tarde para ver los mismos.`
          : `Se ha completado la generación de la muestra [${log.academicYear}-${log.areaName}-${log.period}]`;
    },
    isUniqueAreaTypeSelected() {
      return (
        this.appliedFilters?.areaId &&
        this.appliedFilters?.semesterId &&
        this.appliedFilters?.academicYearId &&
        (this.results || []).length
      );
    },
    hyperlinkConfig() {
      return {
        text: 'Ver Detalle Muestra',
        goTo: (routeProps) => this.goToProviderSampleDetails(routeProps),
      };
    },
    getHyperlinkConfig() {
      return (log) =>
        log.isRunning
          ? {}
          : {
              ...this.hyperlinkConfig,
              goTo: () => this.hyperlinkConfig.goTo({ sampleLogId: log.id }),
            };
    },
    getSampleLogStyles() {
      return (log) => ({
        backgroundColor: log.isRunning ? 'secondary-18' : 'custom-green',
        borderColor: log.isRunning ? 'secondary-39' : 'green',
        color: log.isRunning ? 'secondary-18' : 'green',
        icon: log.isRunning ? 'fa-bell-exclamation' : 'fa-check-circle',
        iconColor: log.isRunning ? 'secondary-18' : 'green',
      });
    },
  },
  async mounted() {
    await this.getInitFilters();
    const { data: groupedSamples } = await _getGroupedSampleExecutionLogs();
    this.sampleLogs = groupedSamples;
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    setFilterModel(filter) {
      this.filterModel = {
        ...this.filterModel,
        ...filter,
      };
    },
    async filterServices(e) {
      const servicestype = (await _getServicesByAreaId(e.id)).data;
      if (e.id == Transportacion) {
        this.selectTrasportation = true;
        this.filterOptions.questionnaireTypes = this.filterOptions.questionnaireTypes.filter(
          (x) => x.id == Maestro
        );
      } else if (e.id != questionnaireArea.TecnicalAssistance) {
        this.selectTrasportation = false;
        await this.getInitFilters();
      }
      this.filterOptions.serviceTypes = servicestype;
    },
    setFilterOptions(options) {
      this.filterOptions = {
        ...this.filterOptions,
        ...options,
      };
    },
    setCreateSampleMode(mode = false) {
      this.isCreateSampleMode = mode;
    },
    async onCreateSample(value) {
      const payload = {
        academicYearId: value.academicYear?.id,
        periodNumber: value.semesterType?.id,
        areaId: value.area?.id,
      };

      try {
        await _validateSampleSelection(payload);
        const answer = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro(a) que desea crear la muestra para el periodo seleccionado?',
          'Si',
          'No'
        );

        if (!answer) return;

        await _createQuestionnaireSample(payload);

        this.sampleLogs.push({
          areaId: value.area?.id,
          areaName: value.area?.name,
          academicYear: value.academicYear?.name,
          period:
            value.semesterType?.id === FirstSemester
              ? '1er Semestre'
              : '2do Semestre',
          isRunning: true,
        });

        this.ShowToast(
          'Proceso Exitoso',
          'Se envió la creación de la muestra exitosamente, esto puede tardar varios minutos, puede regresar más tarde para la confirmación de la muestra.',
          'success'
        );
      } catch (e) {
        this.ShowToast(
          'Error',
          e.response?.data?.message || e.data?.errors?.errors[0],
          'error'
        );
      }
    },
    async getInitFilters() {
      let academicYearId = this.filterModel.academicYear
        ? this.filterModel.academicYear.id
        : this.currentAcademicYearId;
      const response = (await _getInitFilters()).data;
      const providers = await this.getProviders(academicYearId);
      const locationTypes = (await _getServiceLocationTypes()).data;
      const disciplineList = (await _getDisciplineList()).data;
      const porters = (await _getPorters(academicYearId)).data;
      const schoolList = (await _getschool(academicYearId)).data;

      this.filterOptions = {
        ...this.filterOptions,
        ...(response || {}),
        providers,
        locationTypes,
        disciplineList,
        porters,
        schoolList,
        centersOriginal: response.centers,
      };
      if (this.disableCompanyFilter) {
        this.filterModel.provider = this.companyInitialValue;
        if (this.filterModel.provider)
          await this.onChangeProvider(this.filterModel.provider);
      }

      if (this.disableCenterFilter) {
        this.filterModel.center = this.centerInitialValue;
        if (this.filterModel.center)
          await this.onChangeCenter(this.filterModel.center);
      }
    },
    async getProviders(academicYearId) {
      return (await _getProviders(academicYearId)).data;
    },
    async getPorters(academicYearId) {
      return (await _getPorters(academicYearId)).data;
    },
    async getSchool(academicYearId) {
      return (await _getschool(academicYearId)).data;
    },
    async getProviderCenters() {
      if (this.filterModel.academicYear?.id && this.filterModel.provider?.id) {
        this.filterOptions.centers = (
          await _getProviderCenters(
            this.filterModel.academicYear.id,
            this.filterModel.provider.id
          )
        ).data;
      }
    },
    async getProviderMunicipalities() {
      if (
        this.filterModel.academicYear?.id &&
        this.filterModel.provider?.id &&
        this.filterModel.center?.id &&
        this.filterModel.locationType?.id
      ) {
        this.filterOptions.municipalities = (
          await _getProviderMunicipalities(
            this.filterModel.academicYear.id,
            this.filterModel.provider.id,
            this.filterModel.center.id,
            this.filterModel.locationType.id
          )
        ).data;
      }
    },
    async getMunicipalities() {
      if (this.filterModel.academicYear?.id && this.filterModel.center?.id) {
        this.filterOptions.municipalities = (
          await _getMunicipalities(
            this.filterModel.academicYear.id,
            this.filterModel.center.id
          )
        ).data;
      }
    },
    async getProviderLocations() {
      if (
        this.filterModel.academicYear?.id &&
        this.filterModel.municipality?.id &&
        this.filterModel.locationType?.id
      ) {
        this.filterOptions.locationServices = (
          await _getServiceLocations(
            this.filterModel.academicYear.id,
            this.filterModel.provider?.id,
            this.filterModel.municipality.id,
            this.filterModel.locationType.id
          )
        ).data;
      }
    },
    async getProviderSpecialists(academicYearId, providerId) {
      return (await _getProviderSpecialists(academicYearId, providerId)).data;
    },
    async onChangeAcademicYear(value) {
      this.setFilterModel({
        ...this.filterModel,
        provider: null,
        specialist: null,
        locationType: null,
        center: null,
        municipality: null,
        locationService: null,
      });
      this.setFilterOptions({
        centers: this.filterOptions.centersOriginal,
        municipalities: [],
        locationServices: [],
        specialists: [],
      });
      if (value?.id) {
        this.filterOptions.providers = await this.getProviders(value.id);
        this.filterOptions.porters = await this.getPorters(value.id);
        this.filterOptions.schoolList = await this.getSchool(value.id);
      }
    },
    async onChangeProvider(value) {
      const academicYearId = this.filterModel.academicYear?.id;

      this.setFilterModel({
        ...this.filterModel,
        locationType: null,
        center: null,
        municipality: null,
        locationService: null,
        specialist: null,
      });

      this.setFilterOptions({
        centers: this.filterOptions.centersOriginal,
        municipalities: [],
        locationServices: [],
        specialists: [],
      });

      if (value?.id && academicYearId) {
        this.filterOptions.centers = await this.getProviderCenters(
          academicYearId,
          value.id
        );
        this.filterOptions.specialists = await this.getProviderSpecialists(
          academicYearId,
          value.id
        );
        await this.getProviderCenters();
      }
    },
    async onChangeLocationType(value) {
      this.setFilterModel({
        ...this.filterModel,
        center: null,
        municipality: null,
        locationService: null,
      });
      this.setFilterOptions({
        centers: this.filterOptions.centersOriginal,
        municipalities: [],
        locationServices: [],
      });
      if (value?.id) {
        await this.getProviderCenters();
      }
    },
    async onChangeCenter(value) {
      this.setFilterModel({
        ...this.filterModel,
        municipality: null,
      });
      this.setFilterOptions({
        municipalities: [],
        locationServices: [],
      });
      if (value?.id) {
        if (this.filterModel.provider?.id) {
          await this.getProviderMunicipalities();
        } else {
          await this.getMunicipalities();
        }
      }
    },
    async onChangeMunicipality(value) {
      this.filterOptions.locationServices = [];
      this.filterModel.locationService = null;
      if (value?.id) {
        await this.getProviderLocations();
      }
    },
    async onSearchHandler() {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (isValid) {
        this.pagination.currentPage = 1;
        this.searchRequests();
      }
    },
    async searchRequests() {
      if (this.filterModel.area?.id == questionnaireArea.TecnicalAssistance) {
        const payload = {
          academicYearId: this.filterModel.academicYear.id,
          areaId: this.filterModel.area?.id,
          questionnaireTypeId: this.filterModel.questionnaireType?.id,
          statusId: this.filterModel.status?.id,
          ticketNumber: this.filterModel.ticketNumber,
          pageIndex: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
        };
        this.appliedFilters = { ...payload };
        const { data: results } = await _searchTecnicalAssistanceQuestionnaires(
          payload
        );

        this.results = results.data;
        this.pagination = {
          ...this.pagination,
          totalItemCount: results.totalItemCount,
          pageCount: results.pageCount,
          currentPage: results.currentPage,
          count: results.count,
        };
      } else {
        const payload = {
          academicYearId: this.filterModel.academicYear.id,
          semesterId: this.filterModel.semester?.id,
          vendorName: this.filterModel.porter?.name,
          schoolId: this.filterModel.school?.id,
          areaId: this.filterModel.area?.id,
          disciplineName: this.filterModel.disciplineList?.name,
          questionnaireTypeId: this.filterModel.questionnaireType?.id,
          statusId: this.filterModel.status?.id,
          providerId: this.filterModel.provider?.id,
          locationTypeId: this.filterModel.locationType?.id,
          centerId: this.filterModel.center?.id,
          municipalityId: this.filterModel.municipality?.id,
          locationServiceId: this.filterModel.locationService?.id,
          specialistId: this.filterModel.specialist?.id,
          serviceTypeId: this.filterModel.serviceType?.id,
          teacherId: this.filterModel.teacher?.id,
          studentSieId: this.filterModel?.studentSieId,
          isEmailSent: this.filterModel.emailSent?.id
            ? this.filterModel.emailSent?.id === 1
            : null,
          pageIndex: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
        };
        this.appliedFilters = { ...payload };
        const { data: results } = await _search(payload);

        this.results = results.data;
        this.pagination = {
          ...this.pagination,
          totalItemCount: results.totalItemCount,
          pageCount: results.pageCount,
          currentPage: results.currentPage,
          count: results.count,
        };
      }
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      await this.searchRequests();
    },
    async refreshStatusBar() {
      this.showQuestionnaireIndicator = true;
      const payload = {
        academicYearId: this.filterModel.academicYear.id,
      };
      const { data: statusCatalog } = await _getQuestionnaireStatusBarInfo(
        payload
      );
      this.questionnaireStatusCatalog = {
        ...this.questionnaireStatusCatalog,
        pendings: statusCatalog.totalPending,
        completed: statusCatalog.totalCompleted,
        totalCompleted: statusCatalog.completedCount,
      };
    },
    async onCancelHandler(event) {
      let indexToUpdate = this.results.findIndex(
        (submission) => submission.submissionId === event.submissionId
      );
      this.results[indexToUpdate].statusId = event.statusId;
      this.results[indexToUpdate].statusName = event.statusName;
      this.results[indexToUpdate].cancellationComment = event.comment;
    },
    async goToSampleDetailsHandler(appliedFilters) {
      try {
        const { data } = await _getSampleLog(appliedFilters);
        if (data) {
          this.hyperlinkConfig.goTo({ sampleLogId: data.id });
        }
      } catch {
        this.ShowToast('Error', 'Muestra no encontrada.', 'error');
      }
    },
    goToProviderSampleDetails(routeProps) {
      this.$router.push({
        name: 'ServiceSatisfactionQuestionnaireSampleDetails',
        query: { sampleLogId: routeProps.sampleLogId },
      });
    },
  },
};
</script>
